/**
 * One lesson, as a plate in the atlas.
 *
 * The thumbnail floats INSIDE the glass rather than filling the card edge to
 * edge: these frames are cinematic and dark, and letting the glass hold a margin
 * around them is what keeps a grid of them from reading as a wall of black
 * rectangles. Meta sits below the frame in the subject's own hue.
 */

import { Link } from '@tanstack/react-router';
import { Play, Check, GraduationCap } from 'lucide-react';
import { cn } from '../../lib/utils';
import { SubjectArt, subjectMeta, glassPanel, inkMuted } from './learnShared';
import type { CatalogItem } from '../../services/learnAPI';
import type { LessonProgress } from '../../services/learnerAPI';

function fmtDuration(totalSeconds: number): string {
  if (!totalSeconds) return '';
  return `${Math.round(totalSeconds / 60)} min`;
}

export function ExperienceCard({
  item,
  className,
  hasTest,
  progress,
}: {
  item: CatalogItem;
  className?: string;
  hasTest?: boolean;
  progress?: LessonProgress;
}) {
  const meta = subjectMeta(item.subject);
  const dur = fmtDuration(item.totalDuration);
  const completed = progress?.status === 'completed';
  const started = progress && progress.status !== 'not_started';

  return (
    <Link
      to="/learn/$shareToken"
      params={{ shareToken: item.shareToken }}
      className={cn(
        glassPanel,
        'group flex flex-col p-2.5 transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.92] hover:shadow-[0_28px_60px_-30px_rgba(25,24,49,0.5)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B2EE5] dark:hover:bg-[#181A2A]/[0.85]',
        className,
      )}
    >
      <div className="relative aspect-[16/10] overflow-hidden rounded-[15px] bg-[#101018]">
        {item.thumbnailUrl ? (
          <img
            src={item.thumbnailUrl}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <SubjectArt subject={item.subject} />
        )}

        <span className="absolute inset-0 grid place-items-center bg-black/25 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-white/95 shadow-lg">
            <Play className="h-4.5 w-4.5 fill-[#191831] text-[#191831]" />
          </span>
        </span>

        {dur && (
          <span className="absolute bottom-2 right-2 rounded-md bg-black/60 px-1.5 py-0.5 text-[10.5px] font-semibold text-white backdrop-blur-sm">
            {dur}
          </span>
        )}

        {completed && (
          <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-[#17B08B] px-2 py-0.5 text-[10.5px] font-bold text-white shadow">
            <Check className="h-3 w-3" /> Done
          </span>
        )}

        {started && !completed && (
          <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-black/40">
            <span
              className="block h-full"
              style={{ width: `${Math.min(100, Math.max(4, progress!.maxPercent))}%`, background: meta.bright }}
            />
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1.5 px-1.5 pb-1 pt-3">
        <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em]">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: meta.color }} />
          <span style={{ color: meta.color }} className="dark:brightness-[1.6]">
            {item.subject ?? 'Lesson'}
          </span>
          {item.grade && <span className={cn('font-semibold tracking-normal', inkMuted)}>· {item.grade}</span>}
        </div>

        <h3 className="line-clamp-2 min-h-[42px] font-serif text-[18px] leading-[1.32] tracking-tight">
          {item.title ?? 'Untitled lesson'}
        </h3>

        <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1 text-[11.5px] font-semibold">
          {started && !completed && (
            <span style={{ color: meta.color }} className="dark:brightness-[1.6]">
              {progress!.maxPercent}% watched
            </span>
          )}
          {hasTest ? (
            <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5', 'bg-[#5B2EE5]/[0.10] text-[#5B2EE5] dark:bg-[#8B6BF0]/20 dark:text-[#BFA8FA]')}>
              <GraduationCap className="h-3 w-3" /> Has a check
            </span>
          ) : (
            <span className={inkMuted}>no check yet</span>
          )}
        </div>
      </div>
    </Link>
  );
}
